import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Package, Truck, FileCheck, Clock, MapPin, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../data/mock';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-[#F9F9F9] py-4 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#1B5E20]">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#1B5E20]">Products</Link>
            <span>/</span>
            <span className="text-[#1B5E20] font-medium">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Link to="/products">
            <Button variant="outline" className="mb-6 border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white">
              <ArrowLeft className="mr-2" size={16} />
              Back to Products
            </Button>
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Image */}
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6">
                <span className="bg-[#C8A951] text-white px-4 py-2 rounded-full font-semibold shadow-lg">
                  {product.category}
                </span>
              </div>
            </div>

            {/* Details */}
            <div>
              <h1 className="text-4xl font-bold text-[#333333] mb-4">{product.name}</h1>
              <p className="text-lg text-gray-700 mb-8">{product.description}</p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-[#F9F9F9] rounded-lg">
                  <MapPin className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-[#333333] mb-1">Origin</div>
                    <div className="text-gray-700">{product.details.origin}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#F9F9F9] rounded-lg">
                  <Package className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-[#333333] mb-1">Packaging</div>
                    <div className="text-gray-700">{product.details.packaging}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#F9F9F9] rounded-lg">
                  <Truck className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-[#333333] mb-1">Minimum Order Quantity</div>
                    <div className="text-gray-700">{product.details.moq}</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-[#F9F9F9] rounded-lg">
                  <Clock className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <div className="font-semibold text-[#333333] mb-1">Shelf Life</div>
                    <div className="text-gray-700">{product.details.shelfLife}</div>
                  </div>
                </div>
              </div>

              <Link to="/quote">
                <Button size="lg" className="w-full bg-[#C8A951] hover:bg-[#B8994A] text-white font-semibold py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                  Request Quote for This Product
                </Button>
              </Link>
            </div>
          </div>

          {/* Varieties */}
          {product.details.varieties && product.details.varieties.length > 0 && (
            <Card className="mb-8 border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#333333] mb-4">Available Varieties</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {product.details.varieties.map((variety, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-[#F9F9F9] rounded-lg">
                      <Award className="text-[#1B5E20]" size={18} />
                      <span className="text-gray-700">{variety}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certifications */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[#333333] mb-4">Certifications & Documentation</h2>
              <p className="text-gray-600 mb-6">
                All shipments include the following certificates and documentation for smooth customs clearance:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.details.certifications.map((cert, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-[#F9F9F9] rounded-lg border-l-4 border-[#C8A951]">
                    <FileCheck className="text-[#1B5E20] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700 font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Other Products */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#333333] mb-8 text-center">Other Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {products.filter(p => p.id !== product.id).map((otherProduct) => (
              <Card key={otherProduct.id} className="overflow-hidden hover:shadow-xl transition-all border-0 group">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={otherProduct.image}
                    alt={otherProduct.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-xl font-bold text-white">{otherProduct.name}</h3>
                  </div>
                </div>
                <CardContent className="p-4">
                  <Link to={`/products/${otherProduct.slug}`}>
                    <Button variant="outline" className="w-full border-[#1B5E20] text-[#1B5E20] hover:bg-[#1B5E20] hover:text-white">
                      View Details
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
